<template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="isDark ? 'dark' : 'light'"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      @row-clicked="goToCampaign"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { AgEventType, ColDef, ColGroupDef, GridApi, GridOptions, RowEvent } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';
import { Campaign, CampaignRow } from '@/types/campaign.js';
import { useCampaigns } from '@/queries/campaigns.query.js';
import CampaignActions from './cells/CampaignActions.vue';
import { useRoles } from '@/queries/user.query.js';

import codeRomeData from '@/assets/correspondance_rome/correspondance_code_rome.json';
import intituleRomeData from '@/assets/correspondance_rome/correspondance_intitule_rome.json';

interface JobCategorie {
  categorie_number: number,
  generic_categorie: string,
}

type RomeMap = Record<string, JobCategorie>;

const isDark = useDark();
const { locale, t } = useI18n();
const router = useRouter();

let gridApi: GridApi;
const { canWriteCampaigns } = usePermissions();
const { data: campaigns, isPending: isCampaignsPending } = useCampaigns();
const { data: roles } = useRoles();

const codeRome = Object.entries(codeRomeData as RomeMap);
const intituleRome = Object.entries(intituleRomeData as RomeMap);

const noRowsTemplate = ref(`<div>${t('app.tables.campaignsTable.noRowTemplate')}</div>`);

const props = withDefaults(defineProps<{
  search: {
    value: string,
    searchable: string[],
  }
}>(), {
  search: () => {
    return {
      value: "",
      searchable: [
        "name",
      ],
    }
  }
});

const emit = defineEmits<{
  (emit: "edit-campaign", value: Campaign): void;
}>();

const sortedCampaigns = computed(() => {
  if (isCampaignsPending.value) return [];
  const searchString = props.search.value.trim().toLocaleLowerCase();
  if (searchString === "") return [...campaigns.value].sort((a, b) => a.campaign.internal_name.localeCompare(b.campaign.internal_name));

  const filteredRomeIntitule = intituleRome.filter((intitule) => {
    return intitule[0].includes(searchString);
  });

  const filteredRomeCode = codeRome.filter((code) => {
    return code[0].toLocaleLowerCase() === searchString;
  });

  const res = campaigns.value.filter((campaign) => {
    const campaignName = campaign.campaign.internal_name.trim().toLocaleLowerCase();
    if (campaignName.includes(searchString)) return true;
    if (filteredRomeIntitule.find((intitule) => campaignName.includes(intitule[1].generic_categorie))) return true;
    if (filteredRomeCode.find((code) => campaignName.includes(code[1].generic_categorie))) return true;
    return false;
  }).sort((a, b) => a.campaign.internal_name.localeCompare(b.campaign.internal_name));
  return res;
});

const options = computed(() => {
  let columnDefs: (ColDef<CampaignRow, any> | ColGroupDef<CampaignRow>)[]  = [
    { field: 'name', headerName: t('other.table.columns.campaignName'), flex: 1 },
    { field: 'job', headerName: t('other.table.columns.job'), flex: 1 },
    { field: 'date', headerName: t('other.table.columns.createdAt'), flex: 0,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Intl.DateTimeFormat(locale.value, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(params.value));
      },
    },
  ];

  if (canWriteCampaigns.value) {
    columnDefs.push({
      headerName: 'Actions',
      sortable: false,
      filter: false,
      flex: 0,
      headerClass: "header-right",
      cellRenderer: CampaignActions,
      cellRendererParams: {
        onEdit: (row: CampaignRow) => editCampaign(row),
      }
    });
  }

  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
    }),
    suppressCellFocus: true,
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: columnDefs,
  } as GridOptions<CampaignRow>;
});

const rowData = computed(() => {
  if (isCampaignsPending.value) return [];
  return sortedCampaigns?.value?.map((v, index) => {
    return {
      index: index,
      id: v.id,
      name: v.campaign.internal_name,
      job: v.campaign.job_title,
      date: v.created_at,
    } as CampaignRow;
  });
});

function goToCampaign(event: RowEvent<AgEventType, CampaignRow>) {
  router.push({
    name: 'campaign',
    params: {
      campaignId: event.data.id,
    },
  });
}

function editCampaign(row: CampaignRow) {
  if (row?.index == undefined || row?.index === null) return;
  emit('edit-campaign', sortedCampaigns.value[row.index]);
}

function onGridReady(params) {
  gridApi = params.api;
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['date']);
  gridApi.autoSizeColumns(['actions']);
}
</script>

<style lang="scss"></style>